import { useState, useEffect } from "react";
import { __ } from "@wordpress/i18n";
import apiFetch from "@wordpress/api-fetch";
import ContentHeader from "../../components/ContentHeader";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import "./Settings.scss";

const Settings = () => {
	const [isCustomerEnabled, setIsCustomerEnabled] = useState(null);
	const [isFrontendBarEnabled, setIsFrontendBarEnabled] = useState(null);
	const [originalValues, setOriginalValues] = useState({
		customer: null,
		frontendBar: null,
	});
	const [hasChanges, setHasChanges] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [isSaved, setIsSaved] = useState(false);

	useEffect(() => {
		loadSettings();
	}, []);

	// Track changes for both toggles
	useEffect(() => {
		if (
			originalValues.customer !== null &&
			originalValues.frontendBar !== null
		) {
			setHasChanges(
				originalValues.customer !== isCustomerEnabled ||
					originalValues.frontendBar !== isFrontendBarEnabled,
			);
		}
	}, [isCustomerEnabled, isFrontendBarEnabled, originalValues]);

	const handleIsCustomerEnabled = () => {
		setIsCustomerEnabled((prev) => !prev);
	};

	const handleIsFrontendBarEnabled = () => {
		setIsFrontendBarEnabled((prev) => !prev);
	};

	const loadSettings = async () => {
		try {
			const response = await apiFetch({
				path: "/plac/v1/settings",
				method: "GET",
			});
			setIsCustomerEnabled(response.enable_access_control);
			setIsFrontendBarEnabled(response.enable_frontend_bar);
			setOriginalValues({
				customer: response.enable_access_control,
				frontendBar: response.enable_frontend_bar,
			});
		} catch (error) {
			console.error("Error loading settings:", error);
		}
	};

	const saveSettings = async () => {
		setIsSaving(true);
		setIsSaved(false);
		try {
			await apiFetch({
				path: "/plac/v1/settings",
				method: "POST",
				data: {
					_wpnonce: window.placAdminData.nonce,
					enable_access_control: isCustomerEnabled,
					enable_frontend_bar: isFrontendBarEnabled,
				},
			});

			// Update original values after saving
			setOriginalValues({
				customer: isCustomerEnabled,
				frontendBar: isFrontendBarEnabled,
			});
			setHasChanges(false);

			// Show success message for 1.5s
			setTimeout(() => {
				setIsSaving(false);
				setIsSaved(true);

				// Reset success message after 1.5s
				setTimeout(() => {
					setIsSaved(false);
				}, 1500);
			}, 500);
		} catch (error) {
			console.error("Error saving settings:", error);
			setIsSaving(false);
		}
	};

	const discardChanges = () => {
		setIsCustomerEnabled(originalValues.customer);
		setIsFrontendBarEnabled(originalValues.frontendBar);
		setHasChanges(false);
	};

	const isLoading =
		isCustomerEnabled === null || isFrontendBarEnabled === null;

	return (
		<>
			
			<div className="settings-container">
				<ContentHeader
					title={"General Settings"}
					description={
						"The Access Control plugin allows store admins to view their stores from a specific user’s perspective and act as that user."
					}
				/>

				<div className="content-section">
					<div className="leftSection">
						<h2 className="title heading-1 primary-text-color semi-bold">
							{__("Enable Access Control", "access-control")}
						</h2>
						<p className="description heading-2 primary-text-color regular">
							{__(
								"Enable/disable Access Control and the frontend bar to make it easy to switch between users.",
								"access-control",
							)}
						</p>
					</div>

					<div className="rightSection">
						{/* Enable Access Control */}
						<div className="setting-item">
							<div className="input-container">
								<OverlayTrigger
									placement="top"
									overlay={
										<Tooltip id="access-control-tooltip">
											{__(
												"Enable or disable to switch from the WordPress Dashboard.",
												"access-control",
											)}
										</Tooltip>
									}
								>
									<img
										src={`${window.placAdminData.pluginUrl}/assets/images/question.svg`}
										alt="icon"
										className="icon"
									/>
								</OverlayTrigger>
								<div className="input-field">
									<span className="setting-label heading-1 primary-text-color semi-bold">
										{__("Enable Access Control", "access-control")}
									</span>
									{isLoading ? (
										<div className="pl-access-control-switch-placeholder"></div>
									) : (
										<label className="pl-access-control-switch">
											<input
												type="checkbox"
												checked={isCustomerEnabled}
												onChange={
													handleIsCustomerEnabled
												}
											/>
											<span className="pl-access-control-slider round"></span>
										</label>
									)}
								</div>
							</div>
						</div>

						{/* Enable Frontend Bar */}
						<div className="setting-item">
							<div className="input-container">
								<OverlayTrigger
									placement="top"
									overlay={
										<Tooltip id="frontend-bar-tooltip">
											{__(
												"Enable or disable the frontend bar.",
												"access-control",
											)}
										</Tooltip>
									}
								>
									<img
										src={`${window.placAdminData.pluginUrl}/assets/images/question.svg`}
										alt="icon"
										className="icon"
									/>
								</OverlayTrigger>
								<div className="input-field">
									<span className="setting-label heading-1 primary-text-color semi-bold">
										{__("Enable Frontend Bar", "access-control")}
									</span>
									{isLoading ? (
										<div className="pl-access-control-switch-placeholder"></div>
									) : (
										<label className="pl-access-control-switch">
											<input
												type="checkbox"
												checked={isFrontendBarEnabled}
												onChange={
													handleIsFrontendBarEnabled
												}
											/>
											<span className="pl-access-control-slider round"></span>
										</label>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* Footer Section */}
				<div className="footer-section">
					<button
						className={`save-button purple-bg-40 secondary-text-color ${
							!hasChanges ? "disabled" : ""
						}`}
						disabled={isLoading || isSaving || !hasChanges}
						onClick={saveSettings}
					>
						{isSaving ? (
							<div className="saving-changes secondary-text-color">
								<div className="pl-access-control-loading-spinner"></div>
								{__("Changes Saving...", "access-control")}
							</div>
						) : isSaved ? (
							<div className="success-checkmark secondary-text-color">
								<img
									src={`${window.placAdminData.pluginUrl}/assets/images/success-tick.svg`}
									alt="success-icon"
								/>
								{__("Changes Saved", "access-control")}
							</div>
						) : (
							__("Save Changes", "access-control")
						)}
					</button>
					<button
						className={`discard-button purple-text-color btn-border ${
							!hasChanges ? "disabled" : ""
						}`}
						onClick={discardChanges}
					>
						{__("Discard Changes", "access-control")}
					</button>
					{hasChanges && (
						<div className="unsaved-changes">
							<img
								src={`${window.placAdminData.pluginUrl}/assets/images/quotation.svg`}
								alt="quotation-icon"
							/>
							{__("Unsaved Changes", "access-control")}
						</div>
					)}
				</div>
			</div>
		</>
	);
};

export default Settings;
